import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./lib/auth";
import { LangProvider } from "./lib/i18n";
import { ThemeProvider } from "./lib/theme";
import { Navbar } from "./components/site/Navbar";
import { Footer } from "./components/site/Footer";
import { PromoPopup } from "./components/site/PromoPopup";
import { GdprPopup } from "./components/site/GdprPopup";
import Privacy from "./pages/public/Privacy";
import Home from "./pages/public/Home";
import About from "./pages/public/About";
import Clinics from "./pages/public/Clinics";
import EventDetail from "./pages/public/EventDetail";
import Partners from "./pages/public/Partners";
import Contact from "./pages/public/Contact";
import NewsDetail from "./pages/public/NewsDetail";
import Login from "./pages/public/Login";
import Register from "./pages/public/Register";
import { ForgotPassword, ResetPassword } from "./pages/public/ForgotPassword";
import AccountLayout from "./pages/account/AccountLayout";
import { Overview, AccountClinics, History, Messages } from "./pages/account/AccountPages";
import MyRegistrations from "./pages/account/MyRegistrations";
import { Profile, Documents } from "./pages/account/Profile";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard, { ContactRequests } from "./pages/admin/AdminDashboard";
import CrudPage from "./pages/admin/CrudPage";
import AdminEvents from "./pages/admin/AdminEvents";
import EventForm from "./pages/admin/EventForm";
import AdminRegistrants from "./pages/admin/AdminRegistrants";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminComms from "./pages/admin/AdminComms";
import AdminContent, { AdminTranslations } from "./pages/admin/AdminContent";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminDocuments from "./pages/admin/AdminDocuments";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminTaxValues from "./pages/admin/AdminTaxValues";
import { FloatingHelp } from "./components/site/HelpButton";
import { CoffeeBreak } from "./components/site/CoffeeBreak";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import Cart from "./pages/public/Cart";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PublicLayout = () => {
  const { pathname } = useLocation();
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
      {!pathname.startsWith("/account") && <PromoPopup />}
      <GdprPopup />
    </>
  );
};

const CRUD = ["classes", "locations", "instructors", "charters", "partners", "news", "popups", "templates"];

export default function App() {
  return (
    <ThemeProvider>
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollTop />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/clinics" element={<Clinics />} />
              <Route path="/clinics/:id" element={<EventDetail />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/contacts" element={<Contact />} />
              <Route path="/news/:id" element={<NewsDetail />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/account" element={<AccountLayout />}>
                <Route index element={<Overview />} />
                <Route path="clinics" element={<AccountClinics />} />
                <Route path="registrations" element={<MyRegistrations />} />
                <Route path="profile" element={<Profile />} />
                <Route path="documents" element={<Documents />} />
                <Route path="messages" element={<Messages />} />
                <Route path="history" element={<History />} />
              </Route>
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="profile" element={<div className="max-w-4xl"><Profile /></div>} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="events/:id" element={<EventForm />} />
              <Route path="registrants" element={<AdminRegistrants />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="communications" element={<AdminComms />} />
              <Route path="requests" element={<ContactRequests />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="proforma" element={<AdminDocuments key="proforma" kind="proforma" />} />
              <Route path="invoices" element={<AdminDocuments key="invoice" kind="invoice" />} />
              <Route path="discounts" element={<AdminDiscounts />} />
              <Route path="companies" element={<AdminCompanies />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="tax-values" element={<AdminTaxValues />} />
              {CRUD.map((r) => <Route key={r} path={r} element={<CrudPage key={r} resource={r} />} />)}
            </Route>
          </Routes>
          <FloatingHelp />
          <CoffeeBreak />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  );
}
